import { useState, useEffect, useCallback } from 'react';
import { isBrowser } from 'ws-scripts/modules/environment';
import logger from 'ws-scripts/modules/logger';
import { addCoordinatesToLocations } from '../utils/clientGeocoding';

const LOG_PREFIX = '[ws-locations-search][useLocationsData]';
const POLL_INTERVAL_MS = 100;

/**
 * Hook to integrate with ws-locations-data widget.
 */
export const useLocationsData = () => {
	const [locations, setLocations] = useState([]);
	const [error, setError] = useState(null);
	const [isReady, setIsReady] = useState(false);
	const [isLoading, setIsLoading] = useState(true);

	// Check if LocationsData is available (polling + event listener)
	useEffect(() => {
		if (!isBrowser) {
			return () => {};
		}

		const checkReady = () => {
			const hasLocationData = window.DDC?.LocationsData;

			if (hasLocationData) {
				setIsReady(true);
				return true;
			}
			return false;
		};

		// Check immediately
		if (checkReady()) {
			return () => {};
		}

		// Listen for the DDC:LocationsDataReady custom event
		const handleReadyEvent = () => {
			checkReady();
		};
		window.addEventListener('DDC:LocationsDataReady', handleReadyEvent);

		// Keep polling as fallback until LocationsData is available
		const pollInterval = setInterval(() => {
			if (checkReady()) {
				clearInterval(pollInterval);
			}
		}, POLL_INTERVAL_MS);

		// Cleanup
		return () => {
			clearInterval(pollInterval);
			window.removeEventListener(
				'DDC:LocationsDataReady',
				handleReadyEvent
			);
		};
	}, []);

	// Subscribe to location updates
	useEffect(() => {
		if (!isReady || !window.DDC?.LocationsData) {
			return () => {};
		}

		const handleLocationsUpdate = async () => {
			const locationsData = window.DDC.LocationsData;
			const newLocations = locationsData.getLocations() || [];
			const newError = locationsData.getError();

			setLocations(newLocations);
			setError(newError);
			setIsLoading(false);

			// Enhance with coordinates asynchronously in the background
			try {
				const locationsWithCoordinates =
					await addCoordinatesToLocations(newLocations);

				const hasNewCoordinates = locationsWithCoordinates.some(
					(loc, index) => {
						const original = newLocations[index];
						return (
							original &&
							!original.latitude &&
							!original.longitude &&
							loc.latitude &&
							loc.longitude
						);
					}
				);

				if (hasNewCoordinates) {
					setLocations(locationsWithCoordinates);
				}
			} catch (coordinateError) {
				logger.warn(
					`${LOG_PREFIX} Failed to enhance locations with coordinates:`,
					coordinateError
				);
			}
		};

		const unsubscribe =
			window.DDC.LocationsData.registerLocationsNotification(
				handleLocationsUpdate
			);

		// Cleanup subscription on unmount
		return () => {
			if (unsubscribe) {
				unsubscribe();
			}
		};
	}, [isReady]);

	// Function to trigger location search via ws-locations-data
	const setSearchParams = useCallback(
		(postalCode, range, excludeByRange) => {
			if (!isReady || !window.DDC?.LocationsData) {
				logger.warn(
					`${LOG_PREFIX} Cannot set postal code - LocationsData not ready`
				);
				return;
			}

			setIsLoading(true);
			setError(null);
			window.DDC.LocationsData.setSearchParams(
				postalCode,
				range,
				excludeByRange
			);
		},
		[isReady]
	);

	return {
		locations,
		locationCount: locations.length,
		error,
		isReady,
		isLoading,
		setSearchParams
	};
};
